import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
  rem,
  useMantineColorScheme,
} from "@mantine/core";
import {
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
} from "@remix-run/react";
import {
  IconArrowRight,
  IconBook,
  IconBulb,
  IconChartAreaLineFilled,
  IconChartBar,
  IconClipboardCheck,
  IconUsersGroup,
} from "@tabler/icons-react";

import { makeProtectedLoaderWithConfig } from "~/utils/loader.server";

const LOGIN_PATHNAME = "/auth/auth0";
const CONTACT_URL = "https://simsi.com/contact";

const SIMSI_PURPLE = "#6f3fa0";
const SIMSI_PURPLE_LIGHT = "#8f55c8";
const SIMSI_GREEN = "#60ac85";
const SIMSI_INK = "#15131d";
const SIMSI_CREAM = "#f5efe3";

export const loader = makeProtectedLoaderWithConfig(
  { authenticationRequired: false, hubIdRequired: false },
  async ({ currentHubId, user }) => {
    if (user) {
      return redirect("/");
    }
    return { currentHubId, user };
  },
);

const ActionHub = () => {
  const { user } = useLoaderData<typeof loader>();
  const { colorScheme } = useMantineColorScheme();
  const navigate = useNavigate();
  const location = useLocation();
  const showLoginError = location.search.includes("error=auth0");
  const isDark = colorScheme === "dark";

  const features = [
    {
      icon: IconChartAreaLineFilled,
      title: "Focus resources where they can change outcomes",
      description:
        "Move beyond hotspot maps. ActionHub helps turn specific crime patterns, selected places, and environmental conditions into clearer deployment and prevention decisions.",
    },
    {
      icon: IconUsersGroup,
      title: "Coordinate police, city, and community response",
      description:
        "Give every partner the same operating picture. Assign work, align stakeholders, and track whether planned interventions are actually happening in the places that need them.",
    },
    {
      icon: IconClipboardCheck,
      title: "Make prevention work accountable",
      description:
        "Track ownership, field activity, partner follow-through, and outcomes so leaders can see what changed, adapt strategy, and brief progress with confidence.",
    },
    {
      icon: IconBook,
      title: "Support adoption with built-in training",
      description:
        "Train supervisors, analysts, officers, and partners on the workflows behind sustainable place-based prevention.",
    },
  ];

  const workflow = [
    {
      title: "Where should we focus?",
      desc: "Identify priority places and drivers of harm.",
      icon: IconChartAreaLineFilled,
    },
    {
      title: "What is the response?",
      desc: "Coordinate police, city, and community actions.",
      icon: IconUsersGroup,
    },
    {
      title: "Who owns follow-through?",
      desc: "Assign work and monitor implementation.",
      icon: IconClipboardCheck,
    },
    {
      title: "What changed?",
      desc: "Track activity, outcomes, and briefing-ready progress.",
      icon: IconChartBar,
    },
  ];

  const outcomes = [
    {
      icon: IconBulb,
      title: "Priority Places and Drivers",
      body: "Surface the places, patterns, and conditions analysts need to explain deployment, prevention, and partner strategy.",
    },
    {
      icon: IconUsersGroup,
      title: "Assigned Partner Response",
      body: "Put police units, city services, and community partners on the same plan with clear ownership.",
    },
    {
      icon: IconChartBar,
      title: "Brief What Changed",
      body: "Show what was done, where it happened, who owned it, and what changed after implementation.",
    },
  ];

  const proofItems = [
    ["Rutgers University", "Research origin"],
    ["DOJ-recognized approach", "Risk Terrain Modeling"],
    ["85+ peer-reviewed", "Published studies"],
    ["Places, not people", "Environmental risk focus"],
  ];

  return (
    <Box bg={isDark ? "#080810" : "#ffffff"}>
      <Box
        style={{
          position: "relative",
          overflow: "hidden",
          background:
            "radial-gradient(circle at 18% 18%, rgba(111,63,160,0.42), transparent 34%), radial-gradient(circle at 82% 8%, rgba(96,172,133,0.22), transparent 30%), linear-gradient(180deg, #0f0918 0%, #080810 100%)",
          color: "white",
        }}
      >
        <Container size="lg" py={{ base: rem(44), md: rem(64) }}>
          {showLoginError && (
            <Alert color="red" mb={rem(20)}>
              <Text>
                Unable to log in. If you were just invited, make sure you signed
                into the email address your invite was sent to.
              </Text>
            </Alert>
          )}

          <Stack align="center" gap={rem(24)} ta="center">
            <Box
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: rem(8),
                borderRadius: rem(999),
                padding: `${rem(7)} ${rem(13)}`,
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              <Box
                style={{
                  width: rem(7),
                  height: rem(7),
                  borderRadius: rem(999),
                  background: SIMSI_GREEN,
                }}
              />
              <Text
                size="xs"
                fw={800}
                tt="uppercase"
                style={{ letterSpacing: "0.08em", color: SIMSI_CREAM }}
              >
                Place-Based Crime Prevention
              </Text>
            </Box>

            <Title
              order={1}
              style={{
                maxWidth: rem(920),
                fontSize: "clamp(2.25rem, 4.8vw, 4.25rem)",
                lineHeight: 1.02,
                fontWeight: 800,
                color: "white",
              }}
            >
              Make repeat crime prevention accountable.
            </Title>

            <Text
              size="xl"
              style={{
                maxWidth: rem(760),
                color: "rgba(255,255,255,0.74)",
                lineHeight: 1.55,
                fontWeight: 500,
                textWrap: "balance",
              }}
            >
              Turn crime analysis into assigned work and command-ready results.
            </Text>

            <Paper
              radius="xl"
              p={{ base: "md", sm: "lg" }}
              style={{
                maxWidth: rem(760),
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.14)",
                color: "white",
                backdropFilter: "blur(10px)",
              }}
            >
              <Group gap={rem(12)} align="flex-start" wrap="nowrap">
                <ThemeIcon
                  size={42}
                  radius="xl"
                  variant="light"
                  style={{
                    background: "rgba(96,172,133,0.18)",
                    color: SIMSI_GREEN,
                  }}
                >
                  <IconClipboardCheck size={21} />
                </ThemeIcon>
                <Box style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Text fw={800} size="md" mb={4}>
                    A command-ready prevention plan for every priority place
                  </Text>
                  <Text size="sm" style={{ color: "rgba(255,255,255,0.72)" }}>
                    Move from maps and meetings to a live prevention plan: why
                    the place was chosen, who owns each task, what partners
                    committed to, and what changed after action.
                  </Text>
                </Box>
              </Group>
            </Paper>

            <Group gap="md" justify="center">
              {user ? (
                <Button
                  size="lg"
                  radius="md"
                  onClick={() => navigate(LOGIN_PATHNAME)}
                  rightSection={<IconArrowRight size="18" />}
                  style={{
                    background: SIMSI_PURPLE,
                    color: "white",
                    fontWeight: 800,
                  }}
                >
                  Agency Login
                </Button>
              ) : (
                <>
                  <Button
                    size="lg"
                    radius="md"
                    component="a"
                    href={CONTACT_URL}
                    target="_blank"
                    rightSection={<IconArrowRight size="18" />}
                    style={{
                      background: SIMSI_PURPLE,
                      color: "white",
                      fontWeight: 800,
                    }}
                  >
                    Request a command briefing
                  </Button>
                  <Button
                    size="lg"
                    radius="md"
                    variant="outline"
                    onClick={() => navigate(LOGIN_PATHNAME)}
                    style={{
                      color: "white",
                      borderColor: "rgba(255,255,255,0.28)",
                      fontWeight: 700,
                    }}
                  >
                    Agency login
                  </Button>
                </>
              )}
            </Group>

            {!user && (
              <Text
                size="sm"
                style={{
                  maxWidth: rem(640),
                  color: "rgba(255,255,255,0.62)",
                  lineHeight: 1.6,
                }}
              >
                Bring one repeat-crime place or pattern. We will show how
                ActionHub turns it into assigned actions, partner
                accountability, and a command-ready progress brief.
              </Text>
            )}

            <Box
              style={{
                width: "100%",
                maxWidth: rem(980),
                marginTop: rem(20),
                borderRadius: rem(24),
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(255,255,255,0.05)",
                boxShadow: "0 30px 80px rgba(0,0,0,0.34)",
                overflow: "hidden",
                aspectRatio: "16 / 9",
                position: "relative",
              }}
            >
              <Box
                component="img"
                src="https://simsi.com/screenshots/actionhub-crimemap.png"
                alt="ActionHub crime map showing place-based priority areas"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  padding: rem(24),
                  display: "block",
                }}
              />
            </Box>
          </Stack>
        </Container>
      </Box>

      <Box bg={SIMSI_INK} c="white">
        <Container size="lg" py={rem(20)}>
          <Grid gutter="md">
            {proofItems.map(([value, label]) => (
              <Grid.Col key={value} span={{ base: 6, md: 3 }}>
                <Box
                  style={{
                    borderLeft: "1px solid rgba(255,255,255,0.12)",
                    paddingLeft: rem(18),
                  }}
                >
                  <Text fw={800} size="sm">
                    {value}
                  </Text>
                  <Text size="xs" style={{ color: "rgba(255,255,255,0.48)" }}>
                    {label}
                  </Text>
                </Box>
              </Grid.Col>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container size="lg" py={{ base: rem(52), md: rem(76) }}>
        <Grid gutter={rem(48)} align="center">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap={rem(18)}>
              <Text
                size="xs"
                fw={800}
                tt="uppercase"
                style={{ color: SIMSI_PURPLE, letterSpacing: "0.1em" }}
              >
                For public-safety leadership
              </Text>
              <Title
                order={2}
                style={{
                  color: isDark ? "white" : SIMSI_INK,
                  fontSize: "clamp(2rem, 4vw, 3.25rem)",
                  lineHeight: 1.05,
                  fontWeight: 800,
                }}
              >
                CAD and RMS show what happened. ActionHub shows what your team
                is doing to prevent it from happening again.
              </Title>
              <Text
                size="lg"
                style={{
                  color: isDark ? "rgba(255,255,255,0.68)" : "#5f5b68",
                  lineHeight: 1.7,
                }}
              >
                Built around place-based prevention workflows for police
                leaders, analysts, supervisors, city partners, and community
                stakeholders.
              </Text>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper
              radius="xl"
              p="xl"
              withBorder
              style={{
                background: isDark ? "rgba(255,255,255,0.04)" : "#fbfaf7",
                borderColor: isDark
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(21,19,29,0.08)",
              }}
            >
              <Stack gap={rem(18)}>
                <Group gap="sm">
                  <ThemeIcon
                    radius="xl"
                    style={{ background: SIMSI_PURPLE, color: "white" }}
                  >
                    <IconChartAreaLineFilled size={18} />
                  </ThemeIcon>
                  <Box>
                    <Text fw={800}>CAD / 911</Text>
                    <Text size="sm" c="dimmed">
                      Records what happened after the call came in.
                    </Text>
                  </Box>
                </Group>
                <Box
                  style={{
                    height: 1,
                    background: isDark
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(21,19,29,0.08)",
                  }}
                />
                <Group gap="sm">
                  <ThemeIcon
                    radius="xl"
                    style={{ background: SIMSI_GREEN, color: SIMSI_INK }}
                  >
                    <IconClipboardCheck size={18} />
                  </ThemeIcon>
                  <Box>
                    <Text fw={800}>ActionHub</Text>
                    <Text size="sm" c="dimmed">
                      Records assigned police activity, partner follow-through,
                      community engagement, and changes after implementation.
                    </Text>
                  </Box>
                </Group>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Container>

      <Box bg={isDark ? "#10101a" : "#f7f4ef"}>
        <Container size="lg" py={{ base: rem(52), md: rem(68) }}>
          <Stack gap={rem(34)}>
            <Stack align="center" ta="center" gap="sm">
              <Text
                size="xs"
                fw={800}
                tt="uppercase"
                style={{ color: SIMSI_PURPLE, letterSpacing: "0.1em" }}
              >
                Command workflow
              </Text>
              <Title
                order={2}
                style={{
                  maxWidth: rem(760),
                  color: isDark ? "white" : SIMSI_INK,
                  fontSize: "clamp(2rem, 4vw, 3rem)",
                  fontWeight: 900,
                  lineHeight: 1.08,
                }}
              >
                Four steps from crime pattern to accountable action.
              </Title>
            </Stack>

            <Grid gutter={rem(22)}>
              {workflow.map((item, index) => (
                <Grid.Col key={item.title} span={{ base: 12, md: 6 }}>
                  <Paper
                    radius="xl"
                    p="xl"
                    withBorder
                    style={{
                      height: "100%",
                      background: isDark ? "#151523" : "#ffffff",
                      borderColor: isDark
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(21,19,29,0.08)",
                    }}
                  >
                    <Group align="flex-start" gap="md" wrap="nowrap">
                      <Box
                        style={{
                          flexShrink: 0,
                          width: rem(36),
                          height: rem(36),
                          borderRadius: rem(999),
                          background: SIMSI_PURPLE,
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box style={{ flex: 1 }}>
                        <Text fw={800} size="lg" mb={6}>
                          {item.title}
                        </Text>
                        <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                          {item.desc}
                        </Text>
                      </Box>
                    </Group>
                  </Paper>
                </Grid.Col>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      <Container size="lg" py={{ base: rem(52), md: rem(72) }}>
        <Stack gap={rem(34)}>
          <Stack align="center" ta="center" gap="sm">
            <Text
              size="xs"
              fw={800}
              tt="uppercase"
              style={{ color: SIMSI_PURPLE, letterSpacing: "0.1em" }}
            >
              ActionHub outcomes
            </Text>
            <Title
              order={2}
              style={{
                maxWidth: rem(760),
                color: isDark ? "white" : SIMSI_INK,
                fontSize: "clamp(2rem, 4vw, 3rem)",
                fontWeight: 900,
                lineHeight: 1.08,
              }}
            >
              Make prevention work visible before the next command, budget, or
              council question.
            </Title>
          </Stack>
          <Grid gutter={rem(22)}>
            {outcomes.map((item) => (
              <Grid.Col key={item.title} span={{ base: 12, md: 4 }}>
                <Paper
                  radius="xl"
                  p="xl"
                  withBorder
                  style={{
                    height: "100%",
                    borderColor: isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(21,19,29,0.08)",
                    background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                  }}
                >
                  <ThemeIcon
                    variant="light"
                    radius="md"
                    size={42}
                    mb="md"
                    style={{
                      background: "rgba(111,63,160,0.1)",
                      color: SIMSI_PURPLE,
                    }}
                  >
                    <item.icon size={22} />
                  </ThemeIcon>
                  <Text size="lg" fw={800} mb="xs">
                    {item.title}
                  </Text>
                  <Text size="sm" c="dimmed" style={{ lineHeight: 1.65 }}>
                    {item.body}
                  </Text>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        </Stack>
      </Container>

      <Box bg={isDark ? "#10101a" : "#fbfaf7"}>
        <Container size="lg" py={{ base: rem(52), md: rem(72) }}>
          <Stack gap={rem(30)}>
            {features.map((feature) => (
              <Paper
                key={feature.title}
                p="xl"
                radius="xl"
                withBorder
                style={{
                  background: isDark ? "#151523" : "#ffffff",
                  borderColor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(21,19,29,0.08)",
                }}
              >
                <Grid gutter={rem(30)} align="center">
                  <Grid.Col span={{ base: 12, sm: "content" }}>
                    <ThemeIcon
                      radius="xl"
                      size={50}
                      style={{
                        background: "rgba(111,63,160,0.1)",
                        color: SIMSI_PURPLE,
                      }}
                    >
                      <feature.icon size={40} stroke={1.5} />
                    </ThemeIcon>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: "auto" }}>
                    <Title
                      order={3}
                      style={{
                        color: isDark ? "white" : SIMSI_INK,
                        fontSize: rem(24),
                        fontWeight: 800,
                        marginBottom: rem(8),
                      }}
                    >
                      {feature.title}
                    </Title>
                    <Text size="md" c="dimmed" style={{ lineHeight: 1.7 }}>
                      {feature.description}
                    </Text>
                  </Grid.Col>
                </Grid>
              </Paper>
            ))}
          </Stack>
        </Container>
      </Box>

      <Box
        c="white"
        style={{
          background:
            "radial-gradient(circle at 18% 12%, rgba(96,172,133,0.22), transparent 28%), linear-gradient(180deg, #15131d 0%, #080810 100%)",
        }}
      >
        <Container size="md" py={{ base: rem(56), md: rem(76) }}>
          <Stack align="center" gap="lg" ta="center">
            <Title
              order={2}
              style={{
                fontSize: "clamp(2rem, 4vw, 3.4rem)",
                fontWeight: 900,
                lineHeight: 1.05,
                color: "white",
              }}
            >
              See what an accountable prevention plan looks like in ActionHub.
            </Title>
            <Text
              size="lg"
              style={{ color: "rgba(255,255,255,0.72)", maxWidth: rem(620) }}
            >
              Use one current crime pattern to walk through selected places,
              assigned interventions, partner follow-through, and briefing-ready
              results.
            </Text>
            <Button
              size="lg"
              radius="md"
              component="a"
              href={CONTACT_URL}
              target="_blank"
              style={{
                marginTop: rem(10),
                fontWeight: 800,
                background: SIMSI_PURPLE_LIGHT,
                color: "white",
              }}
            >
              Request a command briefing
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export const meta = () => {
  return [{ title: "ActionHub | Accountable Prevention Plans" }];
};

export default ActionHub;
